import React, { useEffect, useRef } from "react";
import { Mic, Square, X } from "lucide-react";
import { toast } from "sonner";
import useGrabador from "@/hooks/useGrabador";
import OndaAudio from "@/components/OndaAudio";

// Grabador estilo Wispr: un toque graba, "LISTO" para y se transcribe solo.
export default function GrabadorPanel({ onEnviar, enviando = false, variant = "grande", onCancelar, autoStart = false }) {
  const g = useGrabador();
  const grande = variant === "grande";
  const arrancado = useRef(false);
  const enviado = useRef(false);

  useEffect(() => {
    if (autoStart && !arrancado.current) { arrancado.current = true; g.start(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (g.estado === "listo" && g.blob && !enviado.current) {
      enviado.current = true;
      if (!g.blob.size || g.blob.size < 800) {
        toast.error("No te escuché. Probá de nuevo, hablando cerca del micrófono.");
        enviado.current = false;
        g.reset();
        return;
      }
      onEnviar(g.blob, () => { enviado.current = false; g.reset(); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.estado, g.blob]);

  const cancelar = () => { enviado.current = true; g.stop(); onCancelar && onCancelar(); };
  const reintentar = () => { arrancado.current = false; enviado.current = false; g.reset(); g.start(); };

  if (g.error) {
    return (
      <div className="text-sm text-crema" data-testid="grabador-error">
        <p className="mb-2">{g.error}</p>
        <button className="btn-fantasma !py-2 !px-4" onClick={reintentar} data-testid="grabador-reintentar">REINTENTAR</button>
      </div>
    );
  }

  // Procesando / transcribiendo (después de tocar LISTO)
  if (enviando || g.estado === "listo") {
    return (
      <div className={grande ? "flex flex-col items-center gap-3 py-2" : "flex items-center gap-3"} data-testid="grabador-transcribiendo">
        <div className={`${grande ? "w-9 h-9" : "w-7 h-7"} rounded-full grad-dorado glow-dorado animate-breathe`} />
        <span className="mono-label text-gris">TRANSCRIBIENDO LO QUE DIJISTE…</span>
      </div>
    );
  }

  // Grabando
  if (g.estado === "grabando") {
    return (
      <div className={grande ? "flex flex-col items-center gap-5" : "flex items-center gap-3 w-full"}>
        <div className="flex items-center gap-4">
          <button onClick={g.stop} className={`${grande ? "w-16 h-16" : "w-11 h-11"} rounded-full bg-rojo/90 flex items-center justify-center animate-breathe`} data-testid="grabador-terminar" aria-label="Listo, transcribir">
            <Square size={grande ? 22 : 16} className="text-white" fill="white" />
          </button>
          <OndaAudio nivel={g.nivel} />
          <span className="mono-label text-crema" data-testid="grabador-tiempo">{g.mmss}</span>
          {!grande && (
            <button className="text-gris hover:text-crema px-1" onClick={cancelar} data-testid="grabador-cancelar" aria-label="Cancelar"><X size={16} /></button>
          )}
        </div>
        {grande && <span className="mono-label text-gris">TOCÁ EL CUADRADO CUANDO TERMINES · SE TRANSCRIBE SOLO</span>}
      </div>
    );
  }

  // idle (variante grande, o cuando no arranca solo)
  return (
    <div className={grande ? "flex flex-col items-center gap-6" : "flex items-center gap-3"}>
      <div className="flex items-center gap-4">
        <button onClick={g.start} className={`${grande ? "w-16 h-16" : "w-11 h-11"} rounded-full grad-dorado glow-dorado flex items-center justify-center`} data-testid="grabador-empezar" aria-label="Grabar">
          <Mic size={grande ? 26 : 18} className="text-negro" />
        </button>
        {grande && <span className="mono-label text-gris">TOCÁ Y HABLÁ · SE TRANSCRIBE SOLO</span>}
      </div>
      {!grande && onCancelar && (
        <button className="mono-label text-gris hover:text-crema" onClick={onCancelar} data-testid="grabador-cerrar">ESCRIBIR</button>
      )}
    </div>
  );
}
